import { useEffect, useState } from 'react'
import { GridPaginationModel } from '@mui/x-data-grid-pro'

/**
 * Defers `paginationModel` changes by one commit so the grid never publishes a
 * `paginationModelChange` while its scroll API is still stale.
 *
 * MUI v6 registers the internal `scrollToIndexes` from an effect, so the copy
 * on the api object always lags the current render by one commit. The copy
 * created on the grid's very first render closes over an `undefined`
 * `virtualScrollerRef` (the ref is only registered later, while `GridBody`
 * renders), and `useGridPagination` runs before `useGridScroll`, so a
 * pagination change published in the grid's second commit reaches that stale
 * callback and throws "Cannot read properties of undefined (reading
 * 'current')". Pages/filters dispatched right after a list mounts land exactly
 * in that window.
 *
 * Holding the model in state and syncing it from an effect pushes any change
 * to a later commit, by which point the registered `scrollToIndexes` is bound
 * to a real `virtualScrollerRef`.
 */
export const useSafePaginationModel = (
  paginationModel?: GridPaginationModel,
) => {
  const [safeModel, setSafeModel] = useState(paginationModel)

  const page = paginationModel?.page
  const pageSize = paginationModel?.pageSize

  useEffect(() => {
    setSafeModel((current) => {
      if (!paginationModel) {
        return current === undefined ? current : undefined
      }
      return current?.page === page && current?.pageSize === pageSize
        ? current
        : paginationModel
    })
    // `paginationModel` is rebuilt on every render by most callers, so key the
    // sync off its values rather than its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize])

  return safeModel
}
